import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Inbox, MessageCircle, IndianRupee, CheckCheck, HandCoins, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const POLL_MS = 15000;
const ICONS = { inquiry: Inbox, interest: Sparkles, chat_message: MessageCircle, payment_received: IndianRupee, payment_confirmed: IndianRupee, payout_paid: HandCoins };

function timeAgo(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Issue F — real-time in-website notification bell (polls every 15s, toasts new arrivals).
export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const latestRef = useRef(null);
  const primedRef = useRef(false);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications", { params: { limit: 20 } });
        if (!alive) return;
        const list = data.items || [];
        const newest = list[0];
        if (primedRef.current && newest && newest.id !== latestRef.current && !newest.read) {
          toast(newest.title, { description: newest.body, action: newest.link ? { label: "Open", onClick: () => navigate(newest.link) } : undefined });
        }
        latestRef.current = newest?.id || null;
        primedRef.current = true;
        setItems(list);
        setUnread(data.unread_count || 0);
      } catch {}
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [user, navigate]);

  if (!user) return null;

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const readAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-md text-[#1A1F2C] hover:bg-[#1A1F2C]/5" aria-label="Notifications" data-testid="notif-bell">
          <Bell size={20} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C85A32] text-white text-[10px] font-semibold grid place-items-center animate-in zoom-in" data-testid="notif-badge">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 rounded-lg border-[#1A1F2C]/10" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1A1F2C]/10">
          <div className="font-display text-lg">Notifications</div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={readAll} className="text-xs h-7" data-testid="notif-read-all"><CheckCheck size={13} className="mr-1" /> Mark all read</Button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#5C6B7A]" data-testid="notif-empty">No notifications yet.</div>
          ) : items.map((n) => {
            const Icon = ICONS[n.kind] || Bell;
            return (
              <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 flex gap-3 border-b border-[#1A1F2C]/5 last:border-0 hover:bg-[#F0EBE1] transition-colors ${n.read ? "" : "bg-[#C85A32]/5"}`} data-testid={`notif-item-${n.id}`}>
                <span className={`shrink-0 w-8 h-8 rounded-full grid place-items-center ${n.read ? "bg-[#1A1F2C]/5 text-[#5C6B7A]" : "bg-[#C85A32]/15 text-[#C85A32]"}`}><Icon size={15} /></span>
                <span className="min-w-0">
                  <span className={`block text-sm leading-snug ${n.read ? "" : "font-semibold"}`}>{n.title}</span>
                  {n.body && <span className="block text-xs text-[#5C6B7A] mt-0.5 line-clamp-2">{n.body}</span>}
                  <span className="block text-[10px] text-[#5C6B7A] mt-1">{timeAgo(n.created_at)}</span>
                </span>
                {!n.read && <span className="ml-auto mt-1.5 w-2 h-2 rounded-full bg-[#C85A32] shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
